import React, { useEffect, useState } from "react";
import { fetchDailyTurnover } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";

const DailyTurnoverChart = ({ date, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [workcellNames, setWorkcellNames] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const { currencySymbol, formatCurrency } = useCustomerCurrency();

  useEffect(() => {
    if (!date) {
      setData([]);
      setWorkcellNames({});
      setIsLoading(false);
      onLoadingChange?.(false);
      return;
    }
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchDailyTurnover(date)
      .then((apiData) => {
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
          setWorkcellNames({});
          setData([]);
          return;
        }
        // Get unique workcell IDs
        const workcells = [...new Set(apiData.map((entry) => entry.workcell_id))];
        const workcellNamesMap = {};
        apiData.forEach((entry) => {
          workcellNamesMap[entry.workcell_id] = entry.workcell_name;
        });
        setWorkcellNames(workcellNamesMap);
        // Build full 24-hour data
        const fullDayData = Array.from({ length: 24 }, (_, i) => {
          const hour = i.toString().padStart(2, "0");
          const entry = { hour };
          workcells.forEach((workcell) => {
            entry[workcell] = 0;
          });
          return entry;
        });
        apiData.forEach((entry) => {
          const index = fullDayData.findIndex((d) => d.hour === entry.hour);
          if (index !== -1) {
            fullDayData[index][entry.workcell_id] = entry.total_turnover;
          }
        });
        setData(fullDayData);
      })
      .catch(() => {
        setWorkcellNames({});
        setData([]);
      })
      .finally(() => {
        setIsLoading(false);
        onLoadingChange?.(false);
      });
  }, [date]);

  const sortedWorkcells = data.length > 0
    ? Object.keys(data[0] || {})
        .filter(key => key !== "hour")
        .sort((a, b) => {
          const totalA = data.reduce((sum, entry) => sum + (entry[a] || 0), 0);
          const totalB = data.reduce((sum, entry) => sum + (entry[b] || 0), 0);
          return totalB - totalA;
        })
    : [];

  const allWorkcellIds = sortedWorkcells.map(Number).sort((a, b) => a - b);

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (data.length === 0) return <div>{t("common.noData")}</div>;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <XAxis dataKey="hour" />
        <YAxis
          label={{
            value: t("daily.overview.turnoverChart.axisLabel", { currency: currencySymbol }),
            angle: -90,
            position: "insideLeft",
            // dy: 60,
            style: { textAnchor: "middle" }
          }}
        />
        <Tooltip formatter={(value) => formatCurrency(value)} />
        {sortedWorkcells.map((workcell, index) => (
          <Bar
            key={workcell}
            dataKey={workcell}
            name={workcellNames[workcell]}
            stackId="a"
            fill={getWorkcellColor(workcell, theme.palette.chart.colors, allWorkcellIds)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DailyTurnoverChart;
